import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { ButtonCustom } from "../form/controls/ButtonCustom";
import { colors } from "../../constants/design.constants";
import { IntegrationService } from "../../service/integration.service";
import {
  isPreviewableArchiveFile,
  parseDelimitedText,
  PREVIEW_MAX_ROWS,
} from "../../utils/csv.util";

const integrationService = new IntegrationService();

interface ArchivePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  filename: string;
  downloadLink: string;
  repo: string;
  workflow: string;
  isLog?: boolean;
}

export const ArchivePreviewModal: React.FC<ArchivePreviewModalProps> = ({
  isOpen,
  onClose,
  filename,
  downloadLink,
  repo,
  workflow,
  isLog,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawText, setRawText] = useState("");
  const [truncated, setTruncated] = useState(false);

  const isDelimited =
    filename.toLowerCase().endsWith(".csv") ||
    filename.toLowerCase().endsWith(".tsv");

  const rows = useMemo(() => {
    if (!rawText || !isDelimited) return [];
    return parseDelimitedText(rawText, PREVIEW_MAX_ROWS);
  }, [rawText, isDelimited]);

  const headers = rows[0] ?? [];
  const bodyRows = rows.slice(1);

  useEffect(() => {
    if (!isOpen || !repo || !workflow || !filename) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setRawText("");
      setTruncated(false);

      if (!isPreviewableArchiveFile(filename)) {
        setError(
          "Preview is not available for this file type. Download the file to view it."
        );
        setLoading(false);
        return;
      }

      try {
        const data = await integrationService.getArchiveFileContent(
          repo,
          workflow,
          filename,
          isLog
        );
        if (!cancelled) {
          setRawText(data?.content ?? "");
          setTruncated(Boolean(data?.truncated));
        }
      } catch {
        if (!cancelled) {
          setError(
            "Unable to load preview. The file may be unavailable — try downloading instead."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, repo, workflow, filename, isLog]);

  const handleDownload = () => {
    window.open(downloadLink, "_blank");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        bg={colors.elevated}
        border={`1px solid ${colors.border}`}
        borderRadius="2px"
        maxH="90vh"
        my="4"
      >
        <Flex
          px="20px"
          py="14px"
          borderBottom={`1px solid ${colors.border}`}
          alignItems="center"
          justifyContent="space-between"
          gap="16px"
          shrink={0}
        >
          <Box minW={0}>
            <Text
              fontWeight="500"
              fontFamily="mono"
              fontSize="13px"
              color={colors.textPrimary}
              noOfLines={1}
              title={filename}
            >
              {filename}
            </Text>
            {!loading && !error && rows.length > 0 && (
              <Text fontSize="11px" color={colors.textTertiary} mt="2px">
                Showing first {Math.min(rows.length, PREVIEW_MAX_ROWS)} rows
                {truncated ? " (file truncated for preview)" : ""}
              </Text>
            )}
          </Box>
          <Flex gap="10px" shrink={0}>
            <ButtonCustom secondary onClick={handleDownload}>
              Download
            </ButtonCustom>
            <ButtonCustom neutral onClick={onClose}>
              Close
            </ButtonCustom>
          </Flex>
        </Flex>

        <ModalBody p="0" position="relative" minH="320px">
          {loading && (
            <Flex alignItems="center" justifyContent="center" minH="320px">
              <Spinner
                thickness="2px"
                speed="0.7s"
                emptyColor={colors.border}
                color={colors.signal}
                size="lg"
              />
            </Flex>
          )}

          {!loading && error && (
            <Flex
              alignItems="center"
              justifyContent="center"
              minH="320px"
              px="24px"
            >
              <Text
                color={colors.textSecondary}
                fontSize="13px"
                textAlign="center"
                maxW="420px"
              >
                {error}
              </Text>
            </Flex>
          )}

          {!loading && !error && isDelimited && rows.length > 0 && (
            <Box overflow="auto" maxH="calc(90vh - 72px)">
              <Table
                size="sm"
                variant="unstyled"
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  minW: "max-content",
                }}
              >
                <Thead position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th
                      bg={colors.panel}
                      borderBottom={`1px solid ${colors.border}`}
                      borderRight={`1px solid ${colors.border}`}
                      color={colors.textTertiary}
                      fontSize="10px"
                      fontWeight="500"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                      px="10px"
                      py="8px"
                      w="48px"
                      minW="48px"
                      position="sticky"
                      left={0}
                      zIndex={2}
                    >
                      #
                    </Th>
                    {headers.map((header, i) => (
                      <Th
                        key={i}
                        bg={colors.panel}
                        borderBottom={`1px solid ${colors.border}`}
                        borderRight={`1px solid ${colors.border}`}
                        color={colors.textSecondary}
                        fontSize="11px"
                        fontWeight="500"
                        textTransform="none"
                        letterSpacing="normal"
                        px="12px"
                        py="8px"
                        whiteSpace="nowrap"
                      >
                        {header || `Column ${i + 1}`}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {bodyRows.map((row, rowIndex) => (
                    <Tr
                      key={rowIndex}
                      _hover={{ bg: colors.hover }}
                      transition="background 0.1s"
                    >
                      <Td
                        bg={colors.elevated}
                        borderBottom={`1px solid ${colors.borderSubtle}`}
                        borderRight={`1px solid ${colors.border}`}
                        color={colors.textTertiary}
                        fontSize="11px"
                        fontFamily="mono"
                        px="10px"
                        py="6px"
                        position="sticky"
                        left={0}
                        zIndex={1}
                      >
                        {rowIndex + 1}
                      </Td>
                      {headers.map((_, colIndex) => (
                        <Td
                          key={colIndex}
                          borderBottom={`1px solid ${colors.borderSubtle}`}
                          borderRight={`1px solid ${colors.borderSubtle}`}
                          color={colors.textPrimary}
                          fontSize="12px"
                          fontFamily="mono"
                          px="12px"
                          py="6px"
                          whiteSpace="nowrap"
                          maxW="360px"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          title={row[colIndex] ?? ""}
                        >
                          {row[colIndex] ?? ""}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {!loading && !error && !isDelimited && rawText && (
            <Box
              as="pre"
              p="20px"
              m="0"
              fontSize="12px"
              fontFamily="mono"
              color={colors.textPrimary}
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              maxH="calc(90vh - 72px)"
              overflow="auto"
            >
              {rawText.split(/\r?\n/).slice(0, PREVIEW_MAX_ROWS).join("\n")}
            </Box>
          )}

          {!loading && !error && isDelimited && rows.length === 0 && !!rawText && (
            <Flex alignItems="center" justifyContent="center" minH="320px">
              <Text color={colors.textSecondary} fontSize="13px">
                File appears to be empty.
              </Text>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
